import { DashboardWidgetType } from "src/modules/administration/types/DashboardWidget";
import appClient from "../../common/services/AppClient";
import GroupedWidget from "../models/GroupedWidget";
import ListWidget from "../models/ListWidget";
import PieChartWidget from "../models/PieChartWidget";
import WidgetBase from "../models/WidgetBase";
import { ActivitiesPeriodEnum, ActivityByProjects } from "../types/Activity";
import NumericWidget from "../models/NumericWidget";
import { UserDashboardSettings } from "../types/DashboardSettings";
import { ErrorStore } from "src/modules/common/stores";
import { UserWidgetData } from "../models/WidgetTypes";
import { endpoints } from "../../../config";

export default class DashboardService {
    constructor(private errorStore: ErrorStore) {
        this.errorStore = errorStore;
    }
    async getActivities(
        period: ActivitiesPeriodEnum,
        projectId: string | null,
    ) {
        let url = `${endpoints.tasksUrl}activity?period=${period}`;
        if (projectId != null) {
            url += `&projectId=${projectId}`;
        }
        return appClient.get<ActivityByProjects>(url);
    }

    async activateWidget(id: string, viewName: string) {
        const url = `${endpoints.tasksUrl}dashboard/widget/${id}/activate?viewName=${viewName}`;
        const resp = await appClient.get<WidgetBase>(url);
        const widget = resp.unwrapOr(null);
        if (widget == null) {
            throw new Error("Not implemented");
        }
        return this.handleWidgetResponse([widget])[0];
    }

    async deactivateWidget(id: string, viewName: string) {
        const url = `${endpoints.tasksUrl}dashboard/widget/${id}/deactivate?viewName=${viewName}`;
        return appClient.post(url);
    }

    async createView(name: string, widgets: string[]) {
        const url = `${endpoints.tasksUrl}dashboard/view`;
        const request = {
            name,
            widgets,
        };
        const resp = await appClient.post<WidgetBase[]>(url, request);
        const unwrappedResp = resp.unwrapOr([]);
        return this.handleWidgetResponse(unwrappedResp);
    }

    async updateView(oldName: string, newName: string, widgets: string[]) {
        const url = `${endpoints.tasksUrl}dashboard/view`;
        const request = {
            oldName,
            newName,
            widgets,
        };
        return appClient.update(url, request);
    }

    async deleteView(name: string) {
        const url = `${endpoints.tasksUrl}dashboard/view/${name}`;
        return appClient.delete(url);
    }

    async getWidgets(name?: string) {
        const url = `${endpoints.tasksUrl}dashboard/widgets?viewName=${name}`;
        const resp = await appClient.get<UserWidgetData>(url);
        const userWidgetsData = resp.unwrapOr({ widgets: [], views: [] });
        const widgets = this.handleWidgetResponse(userWidgetsData.widgets);
        return { widgets, views: userWidgetsData.views };
    }

    async getUserSettings() {
        const url = `${endpoints.tasksUrl}settings/dashboard`;
        const resp = await appClient.get<UserDashboardSettings>(url);
        let settings = null as UserDashboardSettings | null;
        resp.map((r) => {
            settings = r;
        }).mapErr((err) => this.errorStore.addError(err.data));
        return settings;
    }

    async updateWidgetPositions(
        viewName: string,
        positions: { id: string; position: number }[],
    ) {
        const request = {
            viewName,
            widgetPositionUpdate: positions,
        };
        const url = `${endpoints.tasksUrl}dashboard/widgets/position`;
        return appClient.update(url, request);
    }

    isList(w: WidgetBase): w is ListWidget {
        return w.widgetType === DashboardWidgetType.List;
    }

    isPieChart(w: WidgetBase): w is PieChartWidget {
        return w.widgetType === DashboardWidgetType.PieChart;
    }

    isGroupedChart(w: WidgetBase): w is GroupedWidget {
        return w.widgetType === DashboardWidgetType.Grouped;
    }

    isNumeric(w: WidgetBase): w is NumericWidget {
        return w.widgetType === DashboardWidgetType.Numeric;
    }

    private handleWidgetResponse(widgets: WidgetBase[]) {
        return widgets.map((w) => {
            if (this.isList(w)) {
                return new ListWidget(
                    w.id,
                    w.name,
                    w.widgetType,
                    w.tasks,
                    w.projectId,
                    w.isDefaultWidget || false,
                    w.totalTasksCount,
                );
            }
            if (this.isPieChart(w)) {
                return new PieChartWidget(
                    w.id,
                    w.name,
                    w.widgetType,
                    w.chartData,
                    w.projectId,
                    w.isDefaultWidget || false,
                );
            }
            if (this.isGroupedChart(w)) {
                return new GroupedWidget(
                    w.id,
                    w.name,
                    w.widgetType,
                    w.widgets,
                    w.projectId,
                    w.isDefaultWidget || false,
                );
            }
            if (this.isNumeric(w)) {
                return new NumericWidget(
                    w.id,
                    w.name,
                    w.widgetType,
                    w.projectId,
                    w.isDefaultWidget,
                    w.totalTasksCount,
                );
            }

            throw new Error("Not implemented");
        });
    }
}
